import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/blueprints/ui/OSAwareDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/blueprints/ui/OSAwareSelect';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { X, UserPlus, Crown, Globe, Lock } from 'lucide-react';
import { useBlueprintSharing } from '@/hooks/supabase/useBlueprintSharing';

import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';

type ShareableOrganization = {
  id: string;
  name: string;
};

interface ShareBlueprintDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  blueprintId: string;
  blueprintName: string;
  organizations: ShareableOrganization[];
  currentOrganizationId: string | null;
  onShareWithOrganization: (orgId: string | null) => void;
  embeddedInMapleOS?: boolean;
}

export const ShareBlueprintDialog: React.FC<ShareBlueprintDialogProps> = ({
  open,
  onOpenChange,
  blueprintId,
  blueprintName,
  organizations,
  currentOrganizationId,
  onShareWithOrganization,
  embeddedInMapleOS = false,
}) => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { connections, sharedWith, loading, shareBlueprint, unshareBlueprint, updateShareAccess } = useBlueprintSharing(blueprintId);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [accessLevel, setAccessLevel] = useState<'view' | 'edit'>('view');

  const handleShare = () => {
    if (!selectedUserId) return;

    shareBlueprint(selectedUserId, accessLevel);
    setSelectedUserId('');
  };

  const getInitials = (name: string | null | undefined) => {
    return name?.split(' ').map((part) => part[0]).join('').toUpperCase() || '??';
  };

  const ownerProfile = {
    id: user?.id,
    full_name: 'You (Owner)',
    avatar_url: profile?.avatar,
  };

  return (
    <Dialog embedded={embeddedInMapleOS} open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Share "{blueprintName}"</DialogTitle>
          <DialogDescription>Share this blueprint with your connections.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-muted-foreground" />
            <div className="flex-grow">
              <Select embedded={embeddedInMapleOS} value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a user to share with..." />
                </SelectTrigger>
                <SelectContent>
                  {connections.length > 0 ? (
                    connections.map((connection) => (
                      <SelectItem key={connection.id} value={connection.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={connection.avatar_url || undefined} />
                            <AvatarFallback>{getInitials(connection.full_name)}</AvatarFallback>
                          </Avatar>
                          <span>{connection.full_name}</span>
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    <div className="p-2 text-sm text-muted-foreground">No connections found.</div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <Select embedded={embeddedInMapleOS} value={accessLevel} onValueChange={(value: 'view' | 'edit') => setAccessLevel(value)}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="view">Can view</SelectItem>
                <SelectItem value="edit">Can edit</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleShare} disabled={!selectedUserId || loading}>Share</Button>
          </div>
        </div>

        <div className="mt-6 border-t pt-4">
          <h4 className="text-sm font-medium">Share with an Organization</h4>
          <p className="mb-3 text-sm text-muted-foreground">
            Sharing with an organization makes the blueprint visible to all its members.
          </p>
          <Select
            embedded={embeddedInMapleOS}
            value={currentOrganizationId || 'private'}
            onValueChange={(value) => {
              onShareWithOrganization(value === 'private' ? null : value);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select an organization..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="private">
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  <span>Private to you & invited people</span>
                </div>
              </SelectItem>
              {organizations.map((organization) => (
                <SelectItem key={organization.id} value={organization.id}>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    <span>{organization.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-6 space-y-2">
          <h4 className="text-sm font-medium">People with access</h4>
          <ScrollArea className="h-[200px] w-full rounded-md border p-2">
            <div className="space-y-2">
              <div className="flex items-center justify-between rounded-md p-2">
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={ownerProfile.avatar_url} />
                    <AvatarFallback>{getInitials(ownerProfile.full_name)}</AvatarFallback>
                  </Avatar>
                  <span className="font-medium">{ownerProfile.full_name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Crown className="h-4 w-4 text-yellow-500" />
                  <span>Owner</span>
                </div>
              </div>

              {sharedWith.map((sharedUser) => (
                <div key={sharedUser.id} className="flex items-center justify-between rounded-md p-2 hover:bg-muted">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={sharedUser.avatar_url || undefined} />
                      <AvatarFallback>{getInitials(sharedUser.full_name)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{sharedUser.full_name}</p>
                      <p className="text-xs text-muted-foreground">{sharedUser.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                      embedded={embeddedInMapleOS}
                      value={sharedUser.access_level}
                      onValueChange={(value: 'view' | 'edit') => updateShareAccess(sharedUser.share_id, value)}
                    >
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="view">Can view</SelectItem>
                        <SelectItem value="edit">Can edit</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => unshareBlueprint(sharedUser.share_id)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
